'use client';

import { useRef, useState } from 'react';
import Script from 'next/script';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import { trackChatSuppressed, trackChatVendorLoad } from 'helpers/analytics/abTestEvents';

declare global {
  interface Window {
    Quiq?: any;
    duet?: any;
    decagonConfig?: { bottomOffset?: string; horizontalOffset?: string; defaultVisible?: boolean };
  }
}

const DECAGON_SCRIPT_SRC = 'https://decagon.ai/loaders/release/base/v1.js';
const DECAGON_LOADER_ID = 'decagon-chat-loader';

export type ChatVendor = 'quiq' | 'decagon';

export const resolveVendor = (bucket: string | null | undefined): ChatVendor | null => {
  if (bucket === 'quiq' || bucket === 'decagon') return bucket;
  return null;
};

const isVendorAlreadyPresent = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (window.Quiq) return true;
  if (document.getElementById(DECAGON_LOADER_ID)) return true;
  return false;
};

/**
 * DOM id the `ai-transaction-assistant-v30` page configuration binds its widget to (`layout: custom`).
 * That config has no floating launcher of its own, so we render one and give it this id. Quiq attaches
 * its click handler to the element once the widget is ready; a click before that is silently dropped,
 * so the launcher is only rendered after `waitForReady()` resolves.
 */
export const QUIQ_CHAT_BUTTON_ID = 'quiq-chat-button';
export const DECAGON_CHAT_BUTTON_ID = 'decagon-chat-button';

type ChatLauncherProps = {
  id: string;
  onClick?: () => void;
};

/** The floating launcher shared by both vendors: fixed, vertically centred on the right edge. */
const ChatLauncher = ({ id, onClick }: ChatLauncherProps) => (
  <button
    type="button"
    id={id}
    aria-label="Open chat"
    onClick={onClick}
    style={{
      position: 'fixed',
      top: '50%',
      right: 0,
      transform: 'translateY(-50%)',
      zIndex: 2147483000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 56,
      height: 56,
      border: 'none',
      borderRadius: '28px 0 0 28px',
      // Approximated from the design mock; confirm against the brand token.
      background: '#e3e6f8',
      color: '#0b1f6b',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
      cursor: 'pointer',
    }}
  >
    <MessageOutlinedIcon />
  </button>
);

type QuiqChatProps = {
  bucket: string | null | undefined;
  pageConfigurationId: string;
  scriptSrc: string;
  hostname?: string;
};

// Readiness state lives here (not in ChatVendorRouter) so a re-render does not re-run the router's
// double-load guard, which would trip on the window.Quiq this component's own script defines.
const QuiqChat = ({ bucket, pageConfigurationId, scriptSrc, hostname }: QuiqChatProps) => {
  const [ready, setReady] = useState(false);

  const handleLoad = () => {
    if (typeof window === 'undefined' || !window.Quiq) return;
    try {
      const instance = window.Quiq({ pageConfigurationId });
      trackChatVendorLoad({ vendor: 'quiq', bucket, identity: 'none', hostname });
      if (typeof instance?.waitForReady !== 'function') {
        console.warn('Quiq instance has no waitForReady; launcher not rendered');
        return;
      }
      Promise.resolve(instance.waitForReady())
        .then(() => setReady(true))
        .catch((e) => console.warn('Quiq readiness error:', e));
    } catch (e) {
      console.warn('Quiq initialization error:', e);
    }
  };

  return (
    <>
      <Script id="quiq-chat" src={scriptSrc} strategy="afterInteractive" onLoad={handleLoad} />
      {ready && <ChatLauncher id={QUIQ_CHAT_BUTTON_ID} />}
    </>
  );
};

// Must run before the Decagon loader executes. The loader's own launcher is hidden (defaultVisible: false)
// and replaced by ChatLauncher; the iframe stays at the bottom-right corner, where the opened panel belongs.
const setDecagonConfig = (): void => {
  if (typeof window === 'undefined') return;
  window.decagonConfig = { defaultVisible: false, bottomOffset: '0', horizontalOffset: '0' };
};

type DecagonChatProps = {
  bucket: string | null | undefined;
  slug: string;
  hostname?: string;
};

// Same lifecycle as cx-account-ui (config/scripts/decagon.ts): wait for DECAGON_LOADED, hide Decagon's own
// launcher once, then drive the panel with open(). open()/close() are independent of hide()/unhide(), so we
// never re-hide or unhide after that — unhide() would bring Decagon's floating icon back.
// Observed events: minimize, close and close-through-survey all end in DUET_DEINITIALIZE. DECAGON_CLOSE and
// DECAGON_SURVEY_CLOSED are both treated as "chat closed", so our launcher returns on either.
const DECAGON_CLOSED_EVENTS = ['DECAGON_CLOSE', 'DECAGON_SURVEY_CLOSED', 'DUET_DEINITIALIZE'];

const DecagonChat = ({ bucket, slug, hostname }: DecagonChatProps) => {
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  // setEventListener has no unsubscribe, so register exactly once.
  const listening = useRef(false);

  const handleLoad = () => {
    trackChatVendorLoad({ vendor: 'decagon', bucket, identity: 'none', hostname });
    if (listening.current || typeof window.duet?.setEventListener !== 'function') return;
    listening.current = true;
    window.duet.setEventListener((payload: { eventName?: string } | undefined) => {
      const eventName = payload?.eventName;
      if (eventName === 'DECAGON_LOADED') {
        window.duet?.hide?.();
        setReady(true);
      } else if (eventName && DECAGON_CLOSED_EVENTS.includes(eventName)) {
        setOpen(false);
      }
    });
  };

  const handleClick = () => {
    window.duet?.open?.();
    setOpen(true);
  };

  return (
    <>
      <Script
        id={DECAGON_LOADER_ID}
        src={DECAGON_SCRIPT_SRC}
        data-slug={slug}
        strategy="afterInteractive"
        onLoad={handleLoad}
      />
      {ready && !open && <ChatLauncher id={DECAGON_CHAT_BUTTON_ID} onClick={handleClick} />}
    </>
  );
};

export type ChatVendorRouterProps = {
  bucket: string | null | undefined;
  quiqPageConfigurationId: string | undefined;
  quiqScriptSrc: string | undefined;
  decagonSlug: string | undefined;
  hostname?: string;
};

const ChatVendorRouter = ({
  bucket,
  quiqPageConfigurationId,
  quiqScriptSrc,
  decagonSlug,
  hostname,
}: ChatVendorRouterProps) => {
  if (isVendorAlreadyPresent()) {
    trackChatSuppressed({ reason: 'double-load-guard-tripped', hostname });
    return null;
  }

  const vendor = resolveVendor(bucket);

  if (!vendor) {
    trackChatSuppressed({ reason: bucket ? 'unrecognized' : 'no-cookie', hostname });
    return null;
  }

  if (vendor === 'quiq') {
    if (!quiqPageConfigurationId || !quiqScriptSrc) {
      trackChatSuppressed({ reason: 'config-missing', hostname });
      return null;
    }

    return (
      <QuiqChat
        bucket={bucket}
        pageConfigurationId={quiqPageConfigurationId}
        scriptSrc={quiqScriptSrc}
        hostname={hostname}
      />
    );
  }

  if (!decagonSlug) {
    trackChatSuppressed({ reason: 'config-missing', hostname });
    return null;
  }

  setDecagonConfig();

  return <DecagonChat bucket={bucket} slug={decagonSlug} hostname={hostname} />;
};

export default ChatVendorRouter;
